import React, { useState } from 'react';
import { Button } from 'js/design_system/Button';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import FacebookIcon from 'js/design_system/icons/facebook.svg';
import TwitterIcon from 'js/design_system/icons/x_twitter.svg';
import LinkedinIcon from 'js/design_system/icons/linkedin.svg';
import BlueskyIcon from 'js/design_system/icons/bluesky.svg';
import trackClick from 'js/utils/trackClick';
import { currentUser } from 'js/utils/user';

interface ShareButtonsProps {
  submission: {
    id: string | number;
    journal_name: string;
    authors: Array<{
      first_name: string;
      last_name: string;
    }>;
  };
  paperUrl: string;
  marginTop?: number | string;
  source: string;
}

export const ShareButtons: React.FC<ShareButtonsProps> = (props) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const authors = props.submission.authors
    .map((author) => `${author.first_name} ${author.last_name}`)
    .join(', ');

  // Check if current user is one of the authors
  const isCurrentUserAuthor = currentUser
    ? props.submission.authors.some(
        (author) =>
          author.first_name === currentUser!.first_name &&
          author.last_name === currentUser!.last_name,
      )
    : false;

  const shareTextPrefix = isCurrentUserAuthor ? 'My paper' : 'This paper';

  const slugify = (s: string): string =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const [linkedInString, twitterString, blueskyString] = [
    'LinkedIn',
    'Twitter',
    'Bluesky',
  ].map((site) => {
    return encodeURIComponent(
      `${shareTextPrefix} was just published in ${props.submission.journal_name}!
${props.paperUrl}
with ${authors}
#Academic${site} @${slugify(props.submission.journal_name)}`,
    );
  });

  const trackShareClick = (clickType: string) => {
    const clickTrackSuffix = props.paperUrl.includes('#podcast')
      ? '_podcast'
      : '';

    trackClick({
      source: `${props.source}${clickType}${clickTrackSuffix}`,
      entityId: props.submission.id,
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(props.paperUrl);
      trackShareClick('clipboard');
      setCopySuccess(true);

      // Reset to original state after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (_error) {
      // If clipboard API fails, fallback to the old method
      const textArea = document.createElement('textarea');
      textArea.value = props.paperUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      trackShareClick('clipboard');
      setCopySuccess(true);

      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginTop: props.marginTop ?? 32,
      }}
    >
      <Button
        size={'sm'}
        secondary
        icon={copySuccess ? 'check' : 'attachment'}
        onClick={handleCopyLink}
      >
        {copySuccess ? 'Copied' : 'Copy link'}
      </Button>
      <ButtonLink
        size={'sm'}
        icon={<BlueskyIcon />}
        aria-label={'Bluesky'}
        secondary
        href={`https://bsky.app/intent/compose?text=${blueskyString}`}
        onClick={() => {
          trackShareClick('bluesky');
        }}
        target={'_blank'}
      />
      <ButtonLink
        secondary
        size={'sm'}
        icon={<FacebookIcon />}
        aria-label={'Facebook'}
        href={`https://www.facebook.com/sharer/sharer.php?u=${props.paperUrl}`}
        onClick={() => {
          trackShareClick('facebook');
        }}
        target={'_blank'}
      />
      {/* LinkedIn doesn't support the query params for some reason*/}
      <ButtonLink
        aria-label={'LinkedIn'}
        size={'sm'}
        icon={<LinkedinIcon />}
        secondary
        href={`https://www.linkedin.com/shareArticle?text=${linkedInString}`}
        onClick={() => {
          trackShareClick('linkedin');
        }}
        target={'_blank'}
      />
      <ButtonLink
        size={'sm'}
        icon={<TwitterIcon />}
        aria-label={'X / Twitter'}
        secondary
        href={`https://twitter.com/intent/tweet?text=${twitterString}`}
        onClick={() => {
          trackShareClick('twitter');
        }}
        target={'_blank'}
      />
    </div>
  );
};
